import type { Schema } from '@/api/client'

/**
 * The wizard's step metadata: the copy and the URL for each of the six.
 *
 * The **order** is not here — it comes from the server, on every response, in
 * `state.steps`. That is deliberate: `app/onboarding/steps.py` decides what
 * "step 4 of 6" means and the database has a `CHECK` constraint to match, so a
 * second ordering in the SPA would be a third opinion nobody reconciles. This
 * table is keyed by step and holds only what the server has no business knowing:
 * what the screen is called and where it lives in the router.
 */

export type OnboardingState = Schema<'OnboardingState'>
export type StepOut = Schema<'StepOut'>
export type ConnectionOut = Schema<'ConnectionOut'>
export type SuggestionOut = Schema<'SuggestionOut'>

export type StepKey =
  | 'site'
  | 'search_console'
  | 'linkedin'
  | 'sender'
  | 'keywords'
  | 'brand_voice'
  | 'discovery_rules'
  | 'send_window'
  | 'follow_ups'
  | 'run_schedule'
  | 'uptime'
  | 'content_quality'
  | 'publish_target'
  | 'approvals'

export interface StepMeta {
  key: StepKey
  /** The last URL segment. Hyphens, because `search_console` is not a path. */
  slug: string
  /** What the rail calls it — short enough to fit six across at 390px. */
  short: string
  /** The heading on the step's own screen. */
  title: string
  /** What the tenant loses by skipping it. Read by the resume screen. */
  consequence: string
  /** How a finished step reads on the resume list — "Site added", not "Site done". */
  settled: string
  /**
   * How an outstanding step reads on the resume list — "Set up a sender",
   * "Connect Search Console" — `onboarding.html#resume`'s own row labels,
   * which are the act to do rather than the step's heading (parity P-ONBR-01).
   */
  pending: string
  /**
   * What *Skip* says on this step (ONB-3/9/12).
   *
   * S-D2 gives every step its own: "Skip — I'll connect later" on Search
   * Console, "Skip — SEO only" on LinkedIn, a bare "Skip" on the sender. One
   * generic "Skip for now" everywhere costs the tenant the only sentence that
   * says what they are giving up, at the moment they give it up.
   *
   * Here rather than as a prop on `WizardFooter`, per the parity review's S-2:
   * the copy belongs beside the step it describes, and a prop would be six
   * strings scattered across six screens for one footer to reassemble.
   */
  skipLabel: string
  /**
   * What the resume screen's button says for an outstanding step (ONB-22).
   *
   * S-D2's resume list reads "Connect" against the two connectors and "Set up"
   * against the sender, because those are different acts — one leaves for a
   * third party's consent screen, one fills in a form here.
   */
  action: string
  /**
   * Consequence order, lowest first (ONB-24).
   *
   * The resume screen lists outstanding steps by **what is lost**, not by wizard
   * position: S-D2 draws 2, 4, 3 — Search Console, sender, LinkedIn — because a
   * missing data source outranks a missing outreach identity, which outranks an
   * optional lead channel. Server step order is the order you *meet* them, which
   * is a different question from which to do first.
   */
  rank: number
  /**
   * What the progress label reads instead of a time estimate (ONB-8/29).
   *
   * Set only on LinkedIn, which S-D2 labels "Optional" rather than counting: it
   * is the one step whose consequence for SEO is nil, so telling somebody it
   * costs them a minute misstates the choice. A step carrying a note is also not
   * counted by `timeRemaining` below, which is what makes the mockup's
   * 4 / 3 / – / 2 / 1 sequence fall out of a single rule.
   */
  progressNote?: string
}

/**
 * The four tones S-D2 offers, as the tenant chose them.
 *
 * Here rather than in `BrandVoiceStep` because the resume screen shows the
 * chosen voice back, and a slug rendered with its underscores stripped —
 * "professional warm" — reads as a leaked database value rather than as the
 * thing they picked.
 */
export const TONE_LABELS: Record<string, string> = {
  professional_warm: 'Professional and warm',
  direct_technical: 'Direct and technical',
  friendly_casual: 'Friendly and casual',
  formal: 'Formal',
}

export const STEP_META: Record<StepKey, StepMeta> = {
  site: {
    key: 'site',
    slug: 'site',
    short: 'Site',
    title: 'What site should we work on?',
    consequence: 'Nothing can be audited, tracked or written without it.',
    settled: 'Site added',
    pending: 'Add your site',
    skipLabel: 'Skip for now',
    action: 'Add',
    rank: 0,
  },
  search_console: {
    key: 'search_console',
    slug: 'search-console',
    short: 'Search Console',
    title: 'Connect Google Search Console',
    consequence: 'Without this the dashboard has no click or impression data.',
    settled: 'Search Console connected',
    pending: 'Connect Search Console',
    skipLabel: "Skip — I'll connect later",
    action: 'Connect',
    rank: 1,
  },
  linkedin: {
    key: 'linkedin',
    slug: 'linkedin',
    short: 'LinkedIn',
    title: 'Connect LinkedIn for lead discovery',
    consequence: 'Optional — lead discovery only. SEO works without it.',
    settled: 'LinkedIn connected',
    pending: 'Connect LinkedIn',
    skipLabel: 'Skip — SEO only',
    action: 'Connect',
    rank: 3,
    progressNote: 'Optional',
  },
  sender: {
    key: 'sender',
    slug: 'sender',
    short: 'Sender',
    title: 'Who are emails from?',
    consequence: "Outreach can't send until this is done.",
    settled: 'Sender set up',
    pending: 'Set up a sender',
    skipLabel: 'Skip',
    action: 'Set up',
    rank: 2,
  },
  keywords: {
    key: 'keywords',
    slug: 'keywords',
    short: 'Keywords',
    title: 'Which searches should we track?',
    consequence: 'Rank tracking runs with nothing to track.',
    settled: 'Keywords selected',
    pending: 'Choose keywords',
    skipLabel: 'Skip for now',
    action: 'Choose',
    rank: 4,
  },
  brand_voice: {
    key: 'brand_voice',
    slug: 'brand-voice',
    short: 'Voice',
    title: 'How should we sound?',
    consequence: 'Drafts are written in the default voice.',
    settled: 'Brand voice set',
    pending: 'Set your brand voice',
    skipLabel: 'Skip for now',
    action: 'Set up',
    rank: 5,
  },
  // Settings consolidation (2026-09-15). Five settings that used to be edited
  // only on working pages, asked once here and edited under `/settings` after.
  // All optional: Skip keeps the API default, which is what the tenant had
  // before the step existed. `progressNote: 'Optional'` keeps them out of the
  // minutes count for the same reason LinkedIn is — nothing an SEO tenant
  // wanted is lost by skipping one.
  discovery_rules: {
    key: 'discovery_rules',
    slug: 'discovery-rules',
    short: 'Lead rules',
    title: 'What counts as a lead?',
    consequence: 'How old a post may be, and phrases that mean it is not a lead.',
    settled: 'Lead rules set',
    pending: 'Set lead rules',
    skipLabel: 'Skip — keep the default',
    action: 'Continue',
    rank: 6,
    progressNote: 'Optional',
  },
  send_window: {
    key: 'send_window',
    slug: 'send-window',
    short: 'Send window',
    title: 'When may we act on your behalf?',
    consequence: 'Outreach and scheduled posts stay inside these hours.',
    settled: 'Send window set',
    pending: 'Set a send window',
    skipLabel: 'Skip — keep the default',
    action: 'Continue',
    rank: 7,
    progressNote: 'Optional',
  },
  // Batch 2 (2026-09-15): three more found on the first deploy's walkthrough.
  follow_ups: {
    key: 'follow_ups',
    slug: 'follow-ups',
    short: 'Follow-ups',
    title: 'How should we follow up?',
    consequence: 'The rungs a lead climbs, and how many go out in a day.',
    settled: 'Follow-ups set',
    pending: 'Set the follow-up ladder',
    skipLabel: 'Skip — keep the default',
    action: 'Continue',
    rank: 8,
    progressNote: 'Optional',
  },
  run_schedule: {
    key: 'run_schedule',
    slug: 'run-schedule',
    short: 'Schedule',
    title: 'How often should we check your site?',
    consequence: 'Audits and rank checks run on this schedule.',
    settled: 'Schedule set',
    pending: 'Set a schedule',
    skipLabel: 'Skip — keep the default',
    action: 'Continue',
    rank: 9,
    progressNote: 'Optional',
  },
  uptime: {
    key: 'uptime',
    slug: 'uptime',
    short: 'Uptime',
    title: 'Should we watch your site for outages?',
    consequence: 'A plain check from outside AWS that emails you the first time it fails.',
    settled: 'Uptime checks set',
    pending: 'Set uptime checks',
    skipLabel: 'Skip — not watched',
    action: 'Continue',
    rank: 10,
    progressNote: 'Optional',
  },
  content_quality: {
    key: 'content_quality',
    slug: 'content-quality',
    short: 'Quality',
    title: 'How strict is the quality gate?',
    consequence: 'A draft that fails it never reaches your approval queue.',
    settled: 'Quality gate set',
    pending: 'Set the quality gate',
    skipLabel: 'Skip — keep the default',
    action: 'Continue',
    rank: 11,
    progressNote: 'Optional',
  },
  publish_target: {
    key: 'publish_target',
    slug: 'publish-target',
    short: 'Publishing',
    title: 'Where do approved articles go?',
    consequence: 'Nothing can be published until this is connected.',
    settled: 'Publish target set',
    pending: 'Connect a publish target',
    skipLabel: 'Skip — connect later',
    action: 'Continue',
    rank: 12,
    progressNote: 'Optional',
  },
  approvals: {
    key: 'approvals',
    slug: 'approvals',
    short: 'Approvals',
    title: 'What may we change without asking?',
    consequence: 'Everything needs approval until you say otherwise.',
    settled: 'Approvals set',
    pending: 'Set approvals',
    skipLabel: 'Skip — approve everything',
    action: 'Finish setup',
    rank: 13,
    progressNote: 'Optional',
  },
}

/** Every step, in the order the server reported. Unknown keys are dropped, not guessed at. */
export function metaFor(step: string): StepMeta | null {
  // The index signature says every key resolves; the server may send a step this
  // build has never heard of, which is what a deploy mid-release looks like.
  return (STEP_META as Partial<Record<string, StepMeta>>)[step] ?? null
}

export function pathFor(step: string): string {
  const meta = metaFor(step)
  return meta ? `/onboarding/${meta.slug}` : '/onboarding'
}

/** The `connections` row for a provider, or null when nothing has been tried. */
export function connectionFor(state: OnboardingState, provider: string): ConnectionOut | undefined {
  return state.connections.find((row) => row.provider === provider)
}

export function stepStatus(state: OnboardingState, key: StepKey): StepOut | undefined {
  return state.steps.find((row) => row.step === key)
}

/**
 * A `detail` payload, never undefined.
 *
 * `detail` is a Pydantic field with a `default_factory`, which OpenAPI renders as
 * optional — so the generated type says `| undefined` for something the server
 * always sends as at least `{}`. One helper rather than a `?? {}` at every read,
 * because the ones that get forgotten are the ones inside a conditional.
 */
export function detailOf(
  row: { detail?: Record<string, unknown> } | undefined,
): Record<string, unknown> {
  return row?.detail ?? {}
}

/**
 * The one failure reason that is not the tenant's to fix.
 *
 * `app/onboarding/stubs.REASON_NOT_CONFIGURED`: the connector behind a step is
 * not wired on this deployment at all. Every other `failed` is something the
 * tenant did or Google said no to, and the step draws a refusal with a *Try
 * again*. This one has nothing to try — the honest screen is a notice and a
 * Skip, and a rail dot that does not shout `!` at a person who did nothing wrong.
 */
export const REASON_NOT_CONFIGURED = 'not_configured'

export function isNotConfigured(row: { detail?: Record<string, unknown> } | undefined): boolean {
  return detailOf(row).reason === REASON_NOT_CONFIGURED
}

/**
 * Steps that are not **done** — which is not the same as `progress.outstanding`.
 *
 * The server's `outstanding` answers "where will the wizard stop next", so a
 * skipped step is settled and drops out of it. *This* answers "what is not
 * configured", where a skipped step is precisely the thing to say. S-D2 draws
 * both: the progress meter counts settled steps, and the finished screen's
 * "Waiting on you" panel lists the skipped ones by name.
 *
 * Conflating the two is what made the summary say "Nothing — every step is
 * done" to a tenant who had just skipped three, which is how this was found.
 */
export function notConfigured(state: OnboardingState): StepOut[] {
  return state.steps.filter((row) => row.status !== 'done')
}

/**
 * The step's 1-based position in the order the server reported, or 0.
 *
 * Everything below that is "which step am I on" reads through this rather than
 * counting again, so the meter and the label can never disagree about it.
 */
export function positionOf(state: OnboardingState, current: StepKey): number {
  return state.steps.findIndex((row) => row.step === current) + 1
}

/**
 * How wide `.ar-meter__fill` is drawn on a wizard step (ONB-25/28).
 *
 * **The current step counts, and that is the whole change.** The server's
 * `progress.percent` is `settled / total`, which is the honest number for the
 * shell's setup banner — it answers *how much of your setup is done* to somebody
 * who is not in the wizard. Inside the wizard it answers a question nobody
 * asked: standing on step 1 with the form in front of you, the meter read 0 %,
 * and on step 6 with *Finish setup* under your cursor it read 83 %. It never
 * reached 100 % while a step was on screen.
 *
 * S-D2 draws 16 / 33 / 50 / 66 / 83 / 100 across the six, which is exactly
 * `position / total` floored — the same arithmetic as the "Step N of 6" beside
 * it, so the number and the bar are two renderings of one fact rather than two
 * facts that happen to be adjacent.
 *
 * `progress.percent` is left alone and still read by `SetupBanner`; the review's
 * S-4 is explicit that both readings are wanted, in the two places that want
 * them.
 */
export function meterPercent(state: OnboardingState, current: StepKey): number {
  const total = state.steps.length
  if (total === 0) return 0
  const position = positionOf(state, current)
  // A step the server did not report — a deploy mid-release — leaves the bar
  // where the server put it rather than snapping it to zero.
  if (position === 0) return state.progress.percent
  return Math.floor((position * 100) / total)
}

/**
 * The right-hand half of the progress label (ONB-8/29).
 *
 * S-D2 writes "About 4 minutes left" on step 1, "About 3" on step 2, **"Optional"
 * on step 3**, "About 2" on step 4, "About 1 minute left" on step 5 and "Almost
 * done" on step 6. Those six strings are one rule: **a minute for each step
 * ahead of this one that is not the optional connector.** All six are pinned in
 * `wizard.test.tsx`, because "the rule reproduces the mockup" is a claim about
 * every value it produces and not only the ones somebody thought to check.
 *
 * It was `progress.outstanding.length` — how many steps are unsettled anywhere
 * in the wizard, which is a different question and answered "About 6 minutes
 * left" on the step-1 screen the mockup labels 4. It also moved backwards for a
 * returning tenant: settling step 5 dropped the estimate on step 6 to "Almost
 * done" whether or not steps 2 to 4 had ever been answered.
 *
 * `progressNote` is what makes LinkedIn both uncounted and differently labelled,
 * because those are the same fact about it: it is the one step whose absence
 * costs nothing an SEO tenant wanted.
 *
 * **`current` is required.** The first pass made it optional and wrote a
 * fallback branch for "the shell banner's reading" — and the shell banner does
 * not call this: `SetupBanner.tsx` renders the server's `progress` and never
 * asks for a label. So the branch had no caller, and it carried a second
 * spelling of the one-minute string that could never be seen and could never be
 * corrected by the test that pins the six. Found reverifying; deleted rather
 * than given a caller, because the fallback was invented for a requirement that
 * was never there.
 */
export function timeRemaining(state: OnboardingState, current: StepKey): string {
  const note = metaFor(current)?.progressNote
  if (note !== undefined) return note

  const position = positionOf(state, current)
  const ahead = position === 0 ? state.steps : state.steps.slice(position)
  const counted = ahead.filter((row) => metaFor(row.step)?.progressNote === undefined).length
  if (counted === 0) return 'Almost done'
  // "About 1 minute left", which is what S-D2 writes on step 5
  // (`onboarding.html:397`). The pre-existing string was "About a minute left"
  // and it was carried through the rewrite unexamined — the rule was the thing
  // being changed, so the one value the rule produces that is not a number
  // never got compared. Found reverifying.
  if (counted === 1) return 'About 1 minute left'
  return `About ${String(counted)} minutes left`
}

/**
 * Outstanding steps ordered by what is lost, not by wizard position (ONB-24).
 *
 * S-D2's resume list runs 2, 4, 3 — Search Console, sender, LinkedIn — and the
 * app ran 2, 3, 4 because that is the order the server reports steps in. Both
 * orders are defensible in the abstract and only one is drawn: the resume screen
 * exists to answer *what should I do first*, and wizard position answers *what
 * did I meet first*, which for a tenant who skipped three steps is not the same
 * list of priorities.
 *
 * Ties keep server order, so a future step with no rank of its own falls in
 * where the wizard puts it rather than somewhere arbitrary.
 */
export function byConsequence(steps: readonly StepOut[]): StepOut[] {
  return [...steps]
    .map((row, index) => ({ row, index, rank: metaFor(row.step)?.rank ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.row)
}
